<template>
  <article>
    <activity-stage :customRootContainerClass="`${keyboardEnabled ? '' : 'full-height'}`">
      <article slot="content">
        <div class="steps-container">
          <div :class="['step-parent', 'step-parent-active']" v-if="!showPDF">
            <div class="mychoice-instructions">
              <audio-toggle-button></audio-toggle-button>
              <h1>{{ currentRound?.instructions }}</h1>
            </div>
            <div class="image-wrapper">
              <img
                v-if="currentRound?.myChoiceImage?.image?.src"
                :src="currentRound?.myChoiceImage?.image?.src"
                :alt="currentRound?.myChoiceImage?.image?.description"
              />
            </div>
            <label class="question" for="answerWithButton">{{
              currentRound?.myChoiceQuestion
            }}</label>
            <div class="answer">
              <answer-with-button
                :done-callback="finishRound"
                :key="progress.roundIndex"
                :answer="answers[progress.roundIndex]?.answer"
              >
              </answer-with-button>
            </div>
            <div style="text-align: center">
              <button
                v-if="showPlayAgain"
                @click="playAgain"
                class="play-again pure-button"
                transition="fade-05"
              >
                <img :src="playAgainButton" alt="" />
                <span>Play Instructions Again</span>
              </button>
            </div>
          </div>
          <div class="full-screen-row" v-if="!!showPDF">
            <div class="parent" v-if="pdfPreviewSrc">
              <iframe
                :src="pdfPreviewSrc"
                @load="onIframeLoad"
                class="half-screen-column half-print"
                title="Pdf preview"
              >
              </iframe>
              <div class="container-buttons">
                <button
                  v-if="!isMobile"
                  @click="print"
                  :class="['btn-common', showButtons ? 'btn-show' : '']"
                >
                  <div class="action-button-content">Print</div>
                </button>
                <button @click="done" :class="['btn-common', showButtons ? 'btn-show' : '']">
                  <div class="action-button-content">Done</div>
                </button>
              </div>
            </div>
            <div v-else>
              <div class="spinner"></div>
            </div>
          </div>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax } from 'gsap';
import { mapMutations } from 'vuex';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';

import ActivityStage from 'components/activity/ActivityStage.vue';
import AudioToggleButton from 'components/buttons/AudioToggleButton';
import AnswerWithButton from 'components/inputs/AnswerWithButton.vue';
import playAgainButton from 'assets/images/activities/hear-it-type-it/tryAgainButton.png';

pdfMake.vfs = pdfFonts.pdfMake.vfs;
const TIMEOUT = 10000;

export default {
  components: {
    ActivityStage,
    AnswerWithButton,
    AudioToggleButton
  },

  mixins: [AssetLoader, ActivityManager, TypingActivityHelper, ErrorLogger],

  data() {
    return {
      answers: [],
      showPDF: false,
      showButtons: false,
      activityName: 'My Choice Keyboarding',
      answersPdf: null,
      pdfPreviewSrc: null,
      sounds: null,
      showPlayAgain: false,
      currentAudio: null,
      playAgainButton
    };
  },

  computed: {
    currentDate() {
      const now = new Date();
      return `${now.getMonth() + 1}/${now.getDate()}/${now.getFullYear()}`;
    }
  },

  methods: {
    ...mapMutations('error', ['setResponseData']),

    getKeyboardConfig() {
      const { keyboardType } = this.activityData.keyboard || {};

      return {
        keyboardType: keyboardType || 'KeyboardK2',
        keyCodesToNotHandleNatively: ['all'],
        keyCodesToHandleNatively: [9, 13]
      };
    },

    async loadActivity() {
      this.createRounds();
      await Promise.all([this.loadImages(), this.loadSounds()]);
    },

    async loadImages() {
      const imagePaths = this.rounds.map(round => round.myChoiceImage?.image?.src);
      this.queueImage(imagePaths);
      return this.downloadImages();
    },

    async loadSounds() {
      const soundPaths = this.rounds.reduce((accumulator, round) => {
        accumulator.push(
          round.myChoiceAudio?.audio?.src,
          round.myChoiceInstructionsAudio?.audio?.src
        );
        return accumulator;
      }, []);
      this.queueAudio(soundPaths);
      this.sounds = await this.downloadAudio();
    },

    showPlayInstructions() {
      this.showPlayAgain = true;
    },

    startActivity() {
      const self = this;
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);

      this.$nextTick(() => {
        this.currentAudio = this.playCurrentRoundInstructionsAndAfterPlayAudio();
        TweenMax.to('.step-parent-active', 1, {
          opacity: 1,
          onComplete() {
            self.timeout = setTimeout(() => self.showPlayInstructions(), TIMEOUT);
          }
        });
      });
    },

    finishRound(answer) {
      if (this.currentAudio) {
        this.currentAudio.stop();
      }

      const self = this;
      TweenMax.to('.step-parent-active', 1, {
        opacity: 0,
        onComplete() {
          self.answers.push({
            roundIndex: self.progress.roundIndex,
            answer
          });
          self.sndBell();
          if (self.progress.roundIndex + 1 === self.rounds.length) {
            self.setAnswersPdf(self);
            self.showPDF = true;
            self.hideKeyboard();
            TweenMax.to('.step-parent-active', 1, {
              opacity: 1
            });

            return;
          }

          self.setupNewRound();

          self.currentAudio = self.playCurrentRoundInstructionsAndAfterPlayAudio();

          self.$nextTick(() =>
            TweenMax.to('.step-parent-active', 1, {
              opacity: 1,
              onComplete() {
                self.timeout = setTimeout(() => self.showPlayInstructions(), TIMEOUT);
              }
            })
          );
        }
      });
    },

    handleCorrectKey(attempt) {
      clearTimeout(this.timeout);
      this.showPlayAgain = false;
      this.sndType();
      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);
    },

    getAudio(soundPath) {
      return (this.sounds && this.sounds.get(soundPath)) || null;
    },

    playAgain() {
      if (this.showPlayAgain) {
        this.showPlayAgain = false;
        if (this.currentAudio) {
          this.currentAudio.stop();
        }
        this.currentAudio = this.playCurrentRoundInstructionsAndAfterPlayAudio();
      }
    },

    playCurrentRoundInstructionsAndAfterPlayAudio() {
      const audioInstructions = this.getAudio(
        this.currentRound.myChoiceInstructionsAudio?.audio?.src
      );
      const roundAudio = this.getAudio(this.currentRound.myChoiceAudio?.audio?.src);

      const playRoundAudio = () => {
        if (audioInstructions) {
          audioInstructions.off('end', playRoundAudio);
          audioInstructions.off('stop', playRoundAudio);
        }

        if (roundAudio) {
          roundAudio.play();
        }

        return roundAudio;
      };

      if (audioInstructions) {
        this.setInstructionsAudio(audioInstructions);

        audioInstructions.on('end', playRoundAudio);
        audioInstructions.on('stop', playRoundAudio);
        audioInstructions.play();

        return audioInstructions;
      }

      // play step audio otherwise
      return playRoundAudio();
    },

    async getImageDataUrl(url) {
      if (!url) return;
      try {
        const blob = await fetch(url).then(r => r.blob());
        const dataUrl = await new Promise(resolve => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        return dataUrl;
      } catch (err) {
        return null;
      }
    },

    async getPdfDefinition(self) {
      const pageWidth = 300;
      const pdfStepsInfo = await self.rounds?.reduce(async (acc, round, roundIndex) => {
        const image = await this.getImageDataUrl(round.myChoiceImage?.image?.src);
        const { answer } = self.answers.find(ans => ans.roundIndex === roundIndex);
        const resolvedAcc = await acc;

        if (image) {
          resolvedAcc.push({
            image,
            fit: [pageWidth, 150],
            alignment: 'center'
          });
        }
        resolvedAcc.push(
          {
            text: `\n${round.myChoiceQuestion ?? ''}\n\n`
          },
          {
            table: {
              widths: ['100%'],
              body: [[{ text: `${answer}` }]]
            }
          },
          {
            text: '\n\n\n'
          }
        );
        return resolvedAcc;
      }, []);

      const pdfDefinition = {
        pageOrientation: 'portrait',
        pages: 1,
        content: [
          {
            columns: [
              {
                text: 'My Choice Keyboarding',
                alignment: 'left',
                fontSize: 14,
                bold: true
              },
              {
                text: self.currentDate,
                alignment: 'right'
              }
            ]
          },
          ...pdfStepsInfo
        ]
      };
      return pdfDefinition;
    },
    async setAnswersPdf(self) {
      try {
        const pdfDefinition = await self.getPdfDefinition(self);
        self.answersPdf = pdfMake.createPdf(pdfDefinition);
        self.answersPdf.getBlob(blob => {
          self.pdfPreviewSrc = URL.createObjectURL(blob);
          self.$nextTick(() => {
            self.showButtons = true;
          });
        });
      } catch (err) {
        self.pdfPreviewSrc = ' ';
        self.answersPdf = {
          print: () => {}
        };
        self.showButtons = true;
      }
    },
    async print() {
      this.answersPdf.print();
    },
    done() {
      const responseData = this.rounds;

      responseData?.forEach((el, roundIndex) => {
        // eslint-disable-next-line
        el.answer = this.answers.find((obj, index) => index === el.roundIndex)?.answer;
      });

      this.setResponseData(responseData);
      TweenMax.delayedCall(1, this.completeActivity);
    },

    onIframeLoad() {
      const iframe = document.querySelector('iframe');

      if (iframe) {
        const img = (iframe.contentDocument || iframe.contentWindow.document)?.getElementsByTagName(
          'img'
        )?.[0];

        if (img) {
          img.style.width = '100%';
        }
      }
    }
  }
};
</script>

<!-- eslint--disable max-len -->
<style lang="scss" scoped>
@import 'src/assets/sass/fade.scss';

.spinner {
  display: inline-block;
  width: 64px;
  height: 64px;
  position: absolute;
  transform: translate(-50%, -50%);
  left: 50%;
  top: 50%;

  &::after {
    content: ' ';
    display: block;
    width: 46px;
    height: 46px;
    margin: 1px;
    border-radius: 50%;
    border: 5px solid #000;
    border-color: #000 transparent #000 transparent;
    animation: lds-dual-ring 1.2s linear infinite;
  }
}

@keyframes lds-dual-ring {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.btn-common {
  display: none;
  border-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%22210.8%22%20height%3D%2270.3%22%20viewBox%3D%220%200%20210.8%2070.3%22%3E%0A%20%20%3Cpath%20fill%3D%22%23231F20%22%20d%3D%22M.2%2035C.1%2025.3%200%2015.7%200%206c0-1.6.6-3%201.9-4.2C3.2.6%204.6%200%206.3%200c6.6%200%2013.2%200%2019.8.1%206.6.1%2013.2.1%2019.8.1%2013.2.1%2026.4.1%2039.6.1%206.6%200%2013.2-.1%2019.8-.2%206.7%200%2013.3-.1%2019.8-.1%206.7%200%2013.2%200%2019.9.1%206.6.1%2013.2.1%2019.8.1h39.6c1.6%200%203.1.5%204.4%201.7%201.3%201.1%202%202.5%202%204.2%200%204.8-.2%209.7-.5%2014.5-.3%204.8-.4%209.7-.4%2014.5%200%209.7%200%2019.3.1%2029%200%201.7-.5%203-1.6%204.1-1.1%201.1-2.4%201.7-4%201.7-6.6%200-13.2%200-19.8.1s-13.2.1-19.8.1-13.2%200-19.8.1c-6.6.1-13.2.1-19.8.1-6.6%200-13.2%200-19.8-.1-6.6-.1-13.2-.1-19.8-.1-6.6%200-13.2%200-19.8.1-6.6.1-13.2.1-19.8.1-6.6%200-13.2-.1-19.8-.2-6.6-.2-13.2-.3-19.8-.3-1.7%200-3.1-.5-4.2-1.6S.5%2065.7.5%2064.1c0-4.8-.1-9.7-.1-14.5S.2%2039.9.2%2035M6.4%201.8c-1.1%200-2.1.4-3.1%201.3-.9.9-1.4%201.9-1.4%203%200%204.8.1%209.7.2%2014.5.2%204.8.3%209.7.2%2014.5%200%204.8%200%209.7-.1%2014.5s-.1%209.7-.1%2014.5c0%201.1.4%202.1%201.2%202.9.8.8%201.8%201.2%202.9%201.2%206.6%200%2013.2-.1%2019.8-.2%206.6-.2%2013.2-.3%2019.8-.3%206.6%200%2013.2.2%2019.8.5%206.6.3%2013.2.5%2019.8.5%2013.2-.1%2026.4-.2%2039.6-.2%2013.2-.1%2026.4-.2%2039.6-.2%206.6%200%2013.2%200%2019.8-.1%206.8-.2%2013.3-.2%2020-.2%201.1%200%202-.4%202.7-1.2.7-.8%201-1.7%201-2.8%200-4.8%200-9.7.1-14.5.1-4.8.1-9.6.1-14.5s0-9.7-.1-14.5c-.1-4.8-.2-9.7-.2-14.5%200-1.1-.4-2.1-1-3-.7-.9-1.6-1.3-2.6-1.3l-39.6.3c-5.5%200-12.1%200-19.8.2-5.9.1-12.5.1-19.8.1-13.2%200-26.4%200-39.6.2-6.6%200-13.2-.1-19.8-.2-6.6-.1-13.2-.2-19.8-.2-6.6%200-13.2%200-19.8-.1-6.6-.2-13.2-.2-19.8-.2z%22/%3E%0A%3C/svg%3E%0A');
  border-image-slice: 20 20 fill;
  border-image-width: 15px;
  border-image-outset: 0;
  border-image-repeat: stretch;
  border-style: solid;
  text-transform: uppercase;
  font-size: 36px;
  border-radius: 5px;
  background-color: red;
  padding: 10px 40px 5px;
  background-color: white;
  font-family: 'hwtfutura-semibold';
  background-color: #cccccc;
  margin-top: 20px;
  margin-left: auto;
  margin-right: auto;
  width: 300px;

  &.btn-show {
    display: block;
  }
}

.parent {
  display: flex;

  .half-print {
    flex-basis: 300px;

    img {
      width: 100%;
    }
  }

  .container-buttons {
    display: flex;
    flex-basis: calc(100% - 300px);
    justify-content: center;
    text-align: center;
    align-items: center;
  }

  .action-button-content {
    padding-top: 9px;
    text-transform: uppercase;
  }
}

.tablet .parent {
  max-width: 100%;

  .container-buttons {
    flex-basis: auto !important;
    flex-direction: column;
    width: 550px;
  }
}

.image-wrapper {
  height: 130px;
  margin-top: 15px;

  img {
    display: block;
    margin: 0 auto;
    height: 100%;
    width: auto;
  }
}

.question {
  display: flex;
  width: 90%;
  font-size: 17px;
  margin-left: auto;
  margin-right: auto;
  margin-top: 5px;
  margin-bottom: 5px;
}

.answer {
  width: 90%;
  margin-left: auto;
  margin-right: auto;
}

.mychoice-instructions {
  font-size: 18px;
  margin-top: 5px;
  margin-bottom: 3px;
  position: relative;

  button {
    position: absolute;
  }

  h1 {
    font-family: hwtfutura;
    font-size: 18px;
    left: 40px;
    margin: 0px;
    position: relative;
    top: 10px;
    width: calc(100% - 56px);
    -webkit-font-smoothing: auto;
  }
}

.steps-container {
  max-height: 100%;
  width: 100%;
}

.button-horizontal {
  margin: auto auto;
}

.half-screen-column {
  margin: 20px 0px 0px 20px;
  height: 500px;
}

.step-parent {
  opacity: 0;
}

@media print {
  .btn-common {
    display: none;
  }
}

.play-again {
  margin-left: auto;
  margin-right: auto;
  margin-top: 0px;
  text-align: center;
  cursor: pointer;

  img {
    display: inline-block;
    width: 60px;
    height: auto;
  }

  span {
    display: block;
    font-family: 'hwtfutura';
    font-size: 16px;
  }
}
</style>
